import { AdropParamsFactory } from '@/entities/advertisement';
import { useGetBannerAdExchangeQuery } from '@/hooks/advertisement/ad.gql.impl';
import { AdPageType, BannerAdExchangeModel, EAdType, EClickActionType, EColorTheme } from '@/models/ad/list';
import { Client } from '@/types/app';
import { AdColorTheme, ClickActionType, GetBannerAdExchangeQuery } from '@/types/bff/gql-schema';
import { QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

const convertAdColorTheme = (colorTheme: AdColorTheme): EColorTheme => {
  switch (colorTheme) {
    case AdColorTheme.Light:
      return EColorTheme.LIGHT;
    case AdColorTheme.Dark:
      return EColorTheme.DARK;
  }
};

const convertClickActionType = (clickActionType: ClickActionType | null): EClickActionType | null => {
  switch (clickActionType) {
    case ClickActionType.Outlink:
      return EClickActionType.OUT_LINK;
    case ClickActionType.PhoneCall:
      return EClickActionType.PHONE_CALL;
    default:
      return null;
  }
};

const parseBannerAdExchange = (data: GetBannerAdExchangeQuery) => {
  const adExchange = data.getBannerAdExchange;
  if (!adExchange) {
    return null;
  }
  return BannerAdExchangeModel.fromBannerAdExchange({
    bannerAdId: adExchange.bannerAdId,
    bannerImageUrl: adExchange.bannerImageUrl ?? null,
    ctaButtonLabel: adExchange.ctaButtonLabel ?? null,
    colorTheme: convertAdColorTheme(adExchange.colorTheme),
    deliberatedCode: adExchange.deliberatedCode ?? null,
    title: adExchange.title ?? null,
    subTitle: adExchange.subTitle ?? null,
    longTitle: adExchange.longTitle ?? null,
    longSubTitle: adExchange.longSubTitle ?? null,
    clickActionType: convertClickActionType(adExchange.clickAction.clickActionType ?? null),
    outLink: adExchange.clickAction.outlink ?? null,
    phoneNumber: adExchange.clickAction.phoneNumber ?? null,
    targetPlaceCode: adExchange.targetPlaceCode,
    badges: adExchange.badges ?? [],
    content: adExchange.content ?? null,
  });
};

const useGetBannerAdExchange = (targetType: EAdType | null, client: Client, enabled: boolean) => {
  const {
    data: bannerAdExchange = null,
    refetch,
    isInitialLoading,
    dataUpdatedAt,
  } = useGetBannerAdExchangeQuery(
    {
      request: {
        targetPlaceCode: targetType!,
      },
    },
    {
      enabled: targetType !== null && enabled,
      select: parseBannerAdExchange,
      cacheTime: 5000,
      staleTime: 5000,
    }
  );

  useEffect(() => {
    if (bannerAdExchange) {
      bannerAdExchange.setAdViewerOsPlatformByClient(client);
    }
  }, [bannerAdExchange, client]);

  return {
    bannerAdExchange,
    refetchBannerAdExchange: refetch,
    isLoading: isInitialLoading,
    dataUpdatedAt,
  };
};

export default useGetBannerAdExchange;

const getKey = (adPageType: AdPageType, client: Client) => {
  const factory = new AdropParamsFactory(adPageType, client);

  const targetType = factory.getInnerUnitId();
  return targetType ? useGetBannerAdExchangeQuery.getKey({ request: { targetPlaceCode: targetType } }) : [];
};

const prefetchQuery = async (queryClient: QueryClient, adPageType: AdPageType, client: Client) => {
  const factory = new AdropParamsFactory(adPageType, client);

  const targetType = factory.getInnerUnitId();

  if (targetType) {
    await queryClient.prefetchQuery({
      queryKey: useGetBannerAdExchangeQuery.getKey({
        request: {
          targetPlaceCode: targetType,
        },
      }),
      queryFn: useGetBannerAdExchangeQuery.fetcher({ request: { targetPlaceCode: targetType } }),
    });
  }
};

useGetBannerAdExchange.getKey = getKey;
useGetBannerAdExchange.prefetchQuery = prefetchQuery;
